import { getAllStores } from '@api/store';
import PopupComponent from '@components/popup';
import { TCartItem, TInventory, TStore } from '@components/types';
import { cartInventoryFilter } from '@components/variant/variantSelector';
import { cartState } from '@recoil/atoms';
import { ShoppingCart } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';
import { CartItemCard } from './cartitemcard';

interface Props {
    setIsDelivery: React.Dispatch<React.SetStateAction<boolean>>;
    noLink?: boolean
    store?: TStore
    px?: string
    maxWidth?: string
    room?: any
}

export const CartInfo: React.FC<Props> = ({ setIsDelivery, noLink = false, store, px = "md:px-60", maxWidth, room }) => {
    const [cart, setCart] = useRecoilState(cartState);
    const [cartItems, setCartItems] = useState<TCartItem[]>(null);
    const [removeCartItemId, setRemoveCartItemId] = useState<TCartItem>();
    const [showModel, setShowModel] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [stores, setStores] = useState<any>({});

    // Get cart values once outside of any effect - memoize to prevent recreation on every render
    const cartValues = useMemo(() => Array.from(cart.values()), [cart]);

    // Load table info from session storage if needed
    const tableInfo = typeof window !== 'undefined' ? JSON.parse(sessionStorage.getItem('tableInfo')) : null;

    const fetchStoreInformation = async (storeIds: any[]) => {
        if (!storeIds || storeIds?.length === 0) return;

        setIsLoading(true);
        try {
            const storeInfo = await getAllStores(storeIds);
            // Create a map of store_id to store information
            const storeMap: any = {};
            storeInfo?.forEach((store: TStore) => {
                storeMap[store?.id] = store;
            });
            setStores(storeMap);
        } catch (error) {
            console.error("Failed to fetch store information:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Update cartItems when cart changes
    useEffect(() => {
        setCartItems(cartValues);
    }, [cartValues]);

    // Track unique store IDs and fetch store information when they change
    useEffect(() => {
        if (cartValues.length > 0) {
            const uniqueStoreIds = Array.from(
                new Set(cartValues.map(item => item?.inventory?.store_id))
            ).filter(Boolean);

            if (uniqueStoreIds.length > 0) {
                fetchStoreInformation(uniqueStoreIds);
            }
        }
    }, [cartValues]); // Depend on memoized cartValues instead of cart directly

    // Using useEffect to ensure this runs after render, not during
    useEffect(() => {
        const hasDeliverableItems = cartValues.some((item) => item.deliverable && (tableInfo?.table_id == null || tableInfo?.table_id == undefined) && (room?.id == null || room?.id == undefined));
        setIsDelivery(hasDeliverableItems);
    }, [cartValues, setIsDelivery]);

    const removeFromCart = (cartItem: TCartItem) => {
        if (cartItem?.inventory?.variant_id != null) {
            setCart((cart) => {
                const newCart = new Map(cart);
                newCart.delete(cartItem?.internalId);
                return newCart;
            });
        } else {
            let variantId = cartInventoryFilter(cartItem)?.id?.toString();
            setCart((cart) => {
                const newCart = new Map(cart);
                newCart.delete(variantId);
                return newCart;
            });
        }
    };

    const toggleDeliveryMode = (cartItem: TCartItem) => {
        let inventory: TInventory = JSON.parse(JSON.stringify(cartItem?.inventory))
        const finalId = `${inventory?.variant?.id}${inventory?.addons.length > 0 ? "_" + inventory?.addons?.sort((a, b) => a.id - b.id).map((x) => x.id).join("_") : ""}`
        if (cartItem?.inventory?.variant_id != null) {
            setCart((cart) => {
                const newCart = new Map(cart);
                if (newCart.has(finalId)) {
                    let newMode = !newCart.get(finalId).deliverable;
                    newCart.set(finalId, {
                        internalId: finalId,
                        product: cartItem.product,
                        quantity: cartItem.quantity,
                        inventory: cartItem.inventory,
                        deliverable: newMode,
                        addon: cartItem?.addon
                    });
                }
                return newCart;
            });
        } else {
            let variantId = cartInventoryFilter(cartItem)?.id?.toString();
            setCart((cart) => {
                const newCart = new Map(cart);
                if (newCart.has(variantId)) {
                    let newMode = !newCart.get(variantId).deliverable;
                    newCart.set(variantId, {
                        product: cartItem.product,
                        quantity: cartItem.quantity,
                        inventory: cartItem.inventory,
                        deliverable: newMode,
                        addon: cartItem?.addon
                    });
                }
                return newCart;
            });
        }
    };

    // Helper function to get store information for a cart item
    const getStoreForItem = (cartItem: TCartItem) => {
        if (!cartItem?.inventory?.store_id) return null;
        return stores[cartItem?.inventory?.store_id];
    };
    return (
        <div className="font-manrope w-full">
            {/* <Title title="Your Bag/Shopping List" /> */}
            <div className="box-border grid grid-flow-row gap-4 font-manrope w-full">
                {cartItems == null || cartItems?.length == 0 ? (
                    <div className={`box-border flex flex-col items-center justify-center gap-2 p-8 w-full ${px}`}>
                        <div className="flex items-center justify-center w-20 h-20 rounded-full bg-business-100">
                            <ShoppingCart className="w-9 h-9 text-business-500" strokeWidth={1.5} />
                        </div>
                        <h3
                            style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }}
                            className={`text-center md:text-lg text-base font-semibold text-neutral-700 pt-2`}>
                            Your cart is empty
                        </h3>
                        <p
                            style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }}
                            className={`text-center text-sm text-neutral-500`}>Looks like you have not added anything to your cart.</p>
                    </div>
                ) : (
                    cartItems?.map((cartItem) => (
                        <CartItemCard
                            key={cartItem?.inventory?.id}
                            cartItem={cartItem}
                            noLink={noLink}
                            store={store}
                            sellerStore={getStoreForItem(cartItem)}
                            onRemove={(item) => {
                                setRemoveCartItemId(item);
                                setShowModel(true);
                            }}
                        />
                    ))
                )}
            </div>
            <PopupComponent
                showModal={showModel}
                setShowModal={setShowModel}
                ActionFun={() => {
                    removeFromCart(removeCartItemId);
                    setShowModel(false);
                }}
                title="Are you sure you want to remove this item from cart?"
                btnName="YES, REMOVE ITEM"
                closeBtnName="CANCEL"
            />
        </div>
    );
};